/**
 * User, account, auth, onboarding, and GitHub App route constants.
 *
 * Contains routes for the current user (account profile/settings), legacy
 * user routes, authentication flows, onboarding wizard, and the GitHub App
 * callback.
 */

import { PREFIXES, RouteWithPath } from "./routes-platform";

// Onboarding route types
export interface OnboardingRoutes {
  ROOT: RouteWithPath;
  CREATE_ORGANIZATION: RouteWithPath;
  SELECT_ORGANIZATION: RouteWithPath;
  COMPLETE: RouteWithPath;
  getPath: (
    route: "ROOT" | "CREATE_ORGANIZATION" | "SELECT_ORGANIZATION" | "COMPLETE",
  ) => string;
}

// Onboarding routes with metadata
export const ONBOARDING: OnboardingRoutes = {
  ROOT: {
    path: PREFIXES.ONBOARDING,
    title: "Onboarding",
  },
  CREATE_ORGANIZATION: {
    path: `${PREFIXES.ONBOARDING}/create-organization`,
    title: "Create Organization",
  },
  SELECT_ORGANIZATION: {
    path: `${PREFIXES.ONBOARDING}/select-organization`,
    title: "Select Organization",
  },
  COMPLETE: {
    path: `${PREFIXES.ONBOARDING}/complete`,
    title: "Onboarding Complete",
  },

  // Helper function to generate paths
  getPath: (
    route: "ROOT" | "CREATE_ORGANIZATION" | "SELECT_ORGANIZATION" | "COMPLETE",
  ): string => ONBOARDING[route].path,
};

// Auth route type definition to avoid circular references
export interface AuthRoutes {
  ROOT: RouteWithPath;
  LOGIN: RouteWithPath;
  REGISTER: RouteWithPath;
  CALLBACK: RouteWithPath;
  OAUTH_CALLBACK: RouteWithPath;
  getPath: (
    route: "ROOT" | "LOGIN" | "REGISTER" | "CALLBACK" | "OAUTH_CALLBACK",
  ) => string;
}

// Auth routes with metadata
export const AUTH: AuthRoutes = {
  ROOT: {
    path: PREFIXES.AUTH,
    title: "Authentication",
  },
  LOGIN: {
    path: `${PREFIXES.AUTH}/login`,
    title: "Login",
  },
  REGISTER: {
    path: `${PREFIXES.AUTH}/register`,
    title: "Register",
  },
  CALLBACK: {
    path: `${PREFIXES.AUTH}/callback`,
    title: "Auth Callback",
  },
  OAUTH_CALLBACK: {
    path: `${PREFIXES.AUTH}/callbackUrl`,
    title: "OAuth Callback",
  },

  // Helper function to generate paths with params
  getPath: (
    route: "ROOT" | "LOGIN" | "REGISTER" | "CALLBACK" | "OAUTH_CALLBACK",
  ): string => AUTH[route].path,
};

// User route types
export interface UserRoutes {
  ROOT: RouteWithPath;
  PROFILE: RouteWithPath;
  SETTINGS: RouteWithPath;
  getPath: (route: "ROOT" | "PROFILE" | "SETTINGS") => string;
}

// User routes with metadata
export const USER: UserRoutes = {
  ROOT: {
    path: PREFIXES.USER,
    title: "User",
  },
  PROFILE: {
    path: `${PREFIXES.USER}/profile`,
    title: "User Profile",
  },
  SETTINGS: {
    path: `${PREFIXES.USER}/settings`,
    title: "User Settings",
  },

  // Helper function to generate paths with params
  getPath: (route: "ROOT" | "PROFILE" | "SETTINGS"): string => USER[route].path,
};

// GitHub App route types
export interface GitHubRoutes {
  CALLBACK: RouteWithPath;
}

// GitHub App routes
export const GITHUB: GitHubRoutes = {
  CALLBACK: {
    path: "/github/callback",
    title: "GitHub App Callback",
  },
};

// Account route types
export interface AccountRoutes {
  ROOT: RouteWithPath;
  PROFILE: RouteWithPath;
  SETTINGS: RouteWithPath;
  getPath: (route: "ROOT" | "PROFILE" | "SETTINGS") => string;
}

// Account routes - canonical location for current user's own profile/settings
export const ACCOUNT: AccountRoutes = {
  ROOT: {
    path: PREFIXES.ACCOUNT,
    title: "Account",
  },
  PROFILE: {
    path: `${PREFIXES.ACCOUNT}/profile`,
    title: "Profile",
  },
  SETTINGS: {
    path: `${PREFIXES.ACCOUNT}/settings`,
    title: "Account Settings",
  },

  getPath: (route: "ROOT" | "PROFILE" | "SETTINGS"): string =>
    ACCOUNT[route].path,
};
